/**
 * Aba Riscos da ficha da frente (#365, D4): a lista dos riscos aplicados à obra, com a marca dos
 * que valem para esta frente. Salvar substitui o conjunto inteiro. A seleção não salva vive
 * aqui, e a aba fica montada ao trocar de aba, então ela não se perde.
 *
 * Obra sem risco aplicado é pré-requisito ausente: o bloco leva a quem aplica risco e, se a obra
 * não tem setor ligado, avisa que o risco aplicado a ela não alcançaria ninguém.
 */
import React, { useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';
import { Alert, Box, Checkbox, FormControlLabel, Typography } from '@mui/material';

import { PrerequisiteBlock } from '../../../components/common/PrerequisiteBlock';
import { RouteActionButton } from '../../../components';
import { useAcao } from '../../../authorization/useVeredito';
import { pode } from '../../../authorization/pode';
import { workFrontService } from '../../../services/workFrontService';
import { EXPOSURE_LEVEL_LABELS } from '../../../types/riskApplication';
import type { WorkFrontAppliedRisk, WorkFrontDetail, WorkFrontRisksResponse } from '../../../types/workFront';
import { LockableActionButton } from '../../fire-equipments/components/LockableActionButton';
import { FRONT_CLOSED_REASON } from './StagesTab';

const TOUCH_TARGET = 44;

const RISK_TYPE_LABELS: Record<string, string> = {
  PHYSICAL: 'Físico',
  CHEMICAL: 'Químico',
  BIOLOGICAL: 'Biológico',
  ERGONOMIC: 'Ergonômico',
  ACCIDENT: 'Acidente',
  OTHER: 'Outro',
};

const riskDescription = (risk: WorkFrontAppliedRisk): string => {
  const type = RISK_TYPE_LABELS[risk.riskType] ?? risk.riskType;
  if (!risk.degree) return type;
  const degree = (EXPOSURE_LEVEL_LABELS as Record<string, string>)[risk.degree] ?? risk.degree;
  return `${type} · grau ${degree}`;
};

export interface RisksTabProps {
  front: WorkFrontDetail;
  risks: WorkFrontRisksResponse;
  closed: boolean;
  /** A obra não tem setor ligado (só se sabe quando quem lê pode ver a unidade). */
  unitWithoutDepartments: boolean;
  onChanged: () => Promise<void>;
}

export const RisksTab: React.FC<RisksTabProps> = ({ front, risks, closed, unitWithoutDepartments, onChanged }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [selected, setSelected] = useState<Set<number>>(() => new Set(risks.marked));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const cannotUpdate = useAcao('work_front.update', 'Salvar riscos').disabled;
  const locked = closed || cannotUpdate;

  // A seleção só acompanha o servidor quando o conjunto salvo muda de verdade: recarregar a ficha
  // por outro motivo (uma etapa nova) devolve a mesma marcação e não pode apagar a que não foi salva.
  const savedKey = [...risks.marked].sort((a, b) => a - b).join(',');
  useEffect(() => {
    setSelected(new Set(risks.marked));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedKey]);

  const dirty = selected.size !== risks.marked.length || risks.marked.some((id) => !selected.has(id));

  const toggle = (id: number) => {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await workFrontService.replaceRisks(front.id, { riskApplicationIds: [...selected] });
      enqueueSnackbar('Riscos da frente salvos.', { variant: 'success' });
      await onChanged();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Não foi possível salvar os riscos.');
    } finally {
      setSaving(false);
    }
  };

  const noDepartmentsWarning = unitWithoutDepartments && (
    <Alert
      severity="info"
      sx={{ mt: 2 }}
      action={
        <RouteActionButton
          para="/departments"
          exige={pode('department.read')}
          size="small"
          variant="outlined"
          color="inherit"
          sx={{ minHeight: TOUCH_TARGET }}
        >
          Ir para Setores
        </RouteActionButton>
      }
    >
      {`${front.unit.name} não tem setor ligado: um risco aplicado à obra só alcança quem está num setor ligado a ela.`}
    </Alert>
  );

  if (risks.applied.length === 0) {
    return (
      <Box>
        <PrerequisiteBlock
          title={`A ${front.unit.name} ainda não tem riscos aplicados`}
          reason="Os riscos de uma frente são escolhidos entre os que já foram aplicados à obra. Aplique os riscos à obra e volte aqui para marcar os desta frente."
          resolve={{
            label: 'Aplicar risco à obra',
            permission: 'risk_application.create',
            mode: 'navigate',
            route: '/risk-applications',
          }}
          deniedHint={`Peça a um administrador para aplicar riscos à ${front.unit.name}.`}
        />
        {noDepartmentsWarning}
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 0.5 }}>
        {`Marcados entre os ${risks.applied.length} aplicados à ${front.unit.name}`}
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
        Marque os riscos a que esta frente está exposta. Salvar substitui a marcação anterior.
      </Typography>
      {closed && (
        <Typography variant="body1" sx={{ mb: 2 }}>
          {FRONT_CLOSED_REASON}
        </Typography>
      )}

      <Box role="group" aria-label="Riscos aplicados à obra" sx={{ display: 'flex', flexDirection: 'column' }}>
        {risks.applied.map((risk) => (
          <FormControlLabel
            key={risk.id}
            sx={{ minHeight: TOUCH_TARGET, m: 0 }}
            disabled={locked}
            control={<Checkbox checked={selected.has(risk.id)} onChange={() => toggle(risk.id)} />}
            label={
              <Box>
                <Typography variant="body1">{risk.agentName}</Typography>
                <Typography variant="body1" color="text.secondary">
                  {riskDescription(risk)}
                </Typography>
              </Box>
            }
          />
        ))}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2 }}>
        <LockableActionButton
          acao="work_front.update"
          rotulo="Salvar riscos"
          variant="contained"
          color="primary"
          lockedReason={closed ? FRONT_CLOSED_REASON : undefined}
          disabled={!dirty || saving}
          sx={{ minHeight: TOUCH_TARGET }}
          onClick={() => void save()}
        >
          Salvar riscos
        </LockableActionButton>
        {dirty && (
          <Typography variant="body1" role="status">
            Há alterações não salvas.
          </Typography>
        )}
      </Box>
      {noDepartmentsWarning}
    </Box>
  );
};

export default RisksTab;
